/**
 * Shared pieces of "sign in on our own page": a tiny Cognito user-pool API client and the
 * WebAuthn JSON <-> browser object conversion. Used by the admin (owner pool) and the site
 * (families pool); each app keeps its own flow and token storage.
 */

export interface CognitoConfig {
  region: string;
  clientId: string;
}

export class AuthError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Calls one Cognito user-pool API action; maps error types through `friendly`. */
export async function cognitoCall<T>(
  config: CognitoConfig,
  target: string,
  body: Record<string, unknown>,
  friendly: Record<string, string>,
): Promise<T> {
  const res = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const code = String(json.__type ?? 'Error')
      .split('#')
      .pop()!;
    throw new AuthError(code, friendly[code] ?? String(json.message ?? 'Sign-in failed.'));
  }
  return json as T;
}

const fromB64 = (s: string) => {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0)).buffer;
};
const toB64 = (buf: ArrayBuffer | null | undefined) =>
  buf
    ? btoa(String.fromCharCode(...new Uint8Array(buf)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
    : undefined;

type Json = Record<string, unknown>;

export function credentialJson(c: PublicKeyCredential): Json {
  const native = (c as unknown as { toJSON?: () => Json }).toJSON;
  if (native) return native.call(c);
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: toB64(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment ?? undefined,
    clientExtensionResults: c.getClientExtensionResults(),
    response: {
      clientDataJSON: toB64(r.clientDataJSON),
      authenticatorData: 'authenticatorData' in r ? toB64(r.authenticatorData) : undefined,
      signature: 'signature' in r ? toB64(r.signature) : undefined,
      userHandle: 'userHandle' in r ? toB64(r.userHandle) : undefined,
      attestationObject: 'attestationObject' in r ? toB64(r.attestationObject) : undefined,
      transports: typeof r.getTransports === 'function' ? r.getTransports() : undefined,
    },
  };
}

export const passkeysSupported = () => typeof window.PublicKeyCredential === 'function';

/** Runs navigator.credentials.get for Cognito's CREDENTIAL_REQUEST_OPTIONS; returns JSON for Cognito. */
export async function getPasskey(requestOptionsJson: string): Promise<string> {
  const o = JSON.parse(requestOptionsJson) as Json & {
    challenge: string;
    allowCredentials?: { id: string; type: string; transports?: string[] }[];
  };
  const cred = (await navigator.credentials.get({
    publicKey: {
      ...(o as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromB64(o.challenge),
      allowCredentials: o.allowCredentials?.map(
        (a) => ({ ...a, id: fromB64(a.id) }) as PublicKeyCredentialDescriptor,
      ),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new AuthError('Cancelled', 'Passkey sign-in was cancelled.');
  return JSON.stringify(credentialJson(cred));
}

/** Runs navigator.credentials.create for Cognito's StartWebAuthnRegistration options. */
export async function createPasskey(o: Json & { challenge: string; user: Json & { id: string } }) {
  const ex = o.excludeCredentials as { id: string; type: string }[] | undefined;
  const cred = (await navigator.credentials.create({
    publicKey: {
      ...(o as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromB64(o.challenge),
      user: { ...(o.user as unknown as PublicKeyCredentialUserEntity), id: fromB64(o.user.id) },
      excludeCredentials: ex?.map(
        (e) => ({ ...e, id: fromB64(e.id) }) as PublicKeyCredentialDescriptor,
      ),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new AuthError('Cancelled', 'Adding the passkey was cancelled.');
  return credentialJson(cred);
}
